import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Chip,
  Skeleton,
  Button,
  IconButton,
} from "@mui/material";
import { Build, FilterList } from "@mui/icons-material";
import { toast } from "react-toastify";
import theme from "../../theme/theme";
import {
  fetchServiceCalendar,
  updateServiceCalendarEntry,
} from "../../api/ServiceService";
import { fetchProjectPage } from "../../api/ProjectService";
import { fetchEmployeesByDesignation } from "../../api/EmployeeService";
import ServiceCalendar from "../../components/service/ServiceCalendar";
import ServiceEntryDialog from "../../components/service/ServiceEntryDialog";
import AsyncAutocomplete from "../../components/common/AsyncAutocomplete";

const ServiceManagementPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);

  // Filters
  const [filterProjectId, setFilterProjectId] = useState("");
  const [filterEmployeeId, setFilterEmployeeId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Entry dialog
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [savingEntry, setSavingEntry] = useState(false);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const filters = { per_page: 500 };
      if (filterProjectId) filters.project_id = filterProjectId;
      if (filterEmployeeId) filters.service_by = filterEmployeeId;
      if (filterStatus) filters.service_status = filterStatus;

      const response = await fetchServiceCalendar(filters);
      const data = response.data?.data || response.data || [];
      setEntries(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to load service entries");
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await fetchEmployeesByDesignation("service");
      const data = response.data?.data || response.data || [];
      setEmployees(Array.isArray(data) ? data : []);
    } catch {
      // silently fail
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    loadEntries();
  }, [filterProjectId, filterEmployeeId, filterStatus]);

  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
    setEntryDialogOpen(true);
  };

  const handleEntrySave = async (entryId, payload) => {
    setSavingEntry(true);
    try {
      await updateServiceCalendarEntry(entryId, payload);
      toast.success("Service entry updated");
      setEntryDialogOpen(false);
      setSelectedEntry(null);
      await loadEntries();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update entry");
    } finally {
      setSavingEntry(false);
    }
  };

  // Stats
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let pending = 0,
      in_progress = 0,
      completed = 0,
      cancelled = 0,
      delayed = 0,
      unassigned = 0;

    entries.forEach((e) => {
      if (e.service_status === "completed") completed++;
      else if (e.service_status === "in_progress") in_progress++;
      else if (e.service_status === "cancelled") cancelled++;
      else if (e.service_status === "pending") {
        const sd = new Date(e.service_date);
        sd.setHours(0, 0, 0, 0);
        if (sd < today) delayed++;
        else if (!e.service_by) unassigned++;
        else pending++;
      }
    });

    return {
      pending,
      in_progress,
      completed,
      cancelled,
      delayed,
      unassigned,
      total: entries.length,
    };
  }, [entries]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Build sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
        <Box>
          <Typography variant="h5" fontWeight="700">
            Service Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All service schedules across projects
          </Typography>
        </Box>
      </Box>

      {/* Stats Row */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        {[
          { label: "Total", value: stats.total, color: "#546E7A" },
          { label: "Pending", value: stats.pending, color: "#1565C0" },
          { label: "In Progress", value: stats.in_progress, color: "#F57F17" },
          { label: "Completed", value: stats.completed, color: "#2E7D32" },
          { label: "Delayed", value: stats.delayed, color: "#AD1457" },
          { label: "Unassigned", value: stats.unassigned, color: "#E65100" },
          { label: "Cancelled", value: stats.cancelled, color: "#C62828" },
        ].map((s) => (
          <Paper
            key={s.label}
            sx={{
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              textAlign: "center",
              minWidth: 100,
            }}
          >
            <Typography
              variant="h5"
              fontWeight="800"
              sx={{ color: s.color, lineHeight: 1.2 }}
            >
              {s.value}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="600"
            >
              {s.label}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Filters */}
      <Paper
        sx={{
          p: 2.5,
          borderRadius: theme.paper.borderRadius,
          mb: 2.5,
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <FilterList sx={{ color: theme.palette.primary.main }} />
        <Typography
          variant="body2"
          fontWeight="600"
          color="primary"
          sx={{ mr: 1 }}
        >
          Filters
        </Typography>

        <AsyncAutocomplete
          label="Project"
          placeholder="All Projects"
          value={filterProjectId}
          onChange={(id) => setFilterProjectId(id)}
          fetchPage={fetchProjectPage}
          getOptionLabel={(p) =>
            `${p.project_id} — ${p.customer?.name || "N/A"}`
          }
          sx={{ minWidth: 240 }}
        />

        <TextField
          label="Service Employee"
          select
          size="small"
          value={filterEmployeeId}
          onChange={(e) => setFilterEmployeeId(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Employees</MenuItem>
          {employees.map((emp) => (
            <MenuItem key={emp.id} value={emp.id}>
              {emp.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Status"
          select
          size="small"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="in_progress">In Progress</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>

        {(filterProjectId || filterEmployeeId || filterStatus) && (
          <Button
            size="small"
            variant="text"
            onClick={() => {
              setFilterProjectId("");
              setFilterEmployeeId("");
              setFilterStatus("");
            }}
            sx={{ textTransform: "none", color: theme.palette.primary.main }}
          >
            Clear Filters
          </Button>
        )}
      </Paper>

      {/* Calendar */}
      <Paper sx={{ p: 3, borderRadius: theme.paper.borderRadius }}>
        <ServiceCalendar
          entries={entries}
          loading={loading}
          onEntryClick={handleEntryClick}
        />
      </Paper>

      {/* Entry Dialog */}
      <ServiceEntryDialog
        open={entryDialogOpen}
        onClose={() => {
          setEntryDialogOpen(false);
          setSelectedEntry(null);
        }}
        entry={selectedEntry}
        employees={employees}
        onSave={handleEntrySave}
        saving={savingEntry}
      />
    </Box>
  );
};

export default ServiceManagementPage;
